import { useState } from 'react'
import { BottomSheet, BottomSheetProps } from 'react-spring-bottom-sheet'
import { lakshmi } from '@machobear/design-system/themes/lakshmi'
import { ThemeProvider } from '@machobear/design-system'
import { themeProviderOptions } from '@/lib/themes'
import { useModalize } from '../'
import 'react-spring-bottom-sheet/dist/style.css'
import './style.css'

const defaultSnap: BottomSheetProps['defaultSnap'] = ({ maxHeight, minHeight }) => {
  return Math.min(minHeight, maxHeight - maxHeight * (9 / 100))
}

// We divide the viewport into 3 snap points.
// If the content is greater than the snap point, we include it.
// The top snap point is either the max content of the sheet,
// or close to 100% of the viewport if it needs to scroll.
const snapPoints: BottomSheetProps['snapPoints'] = ({ maxHeight, minHeight }) => {
  const top = Math.min(minHeight, maxHeight - maxHeight * (9 / 100))
  const middle = maxHeight - maxHeight * (30 / 100)
  const bottom = maxHeight - maxHeight * (45 / 100)
  return [top, ...(minHeight > middle ? [middle] : []), ...(minHeight > bottom ? [bottom] : [])]
}

const topSnap: BottomSheetProps['snapPoints'] = ({ maxHeight, minHeight }) => {
  const top = Math.min(minHeight, maxHeight - maxHeight * (9 / 100))
  return [top]
}

type SheetProps = {
  children: React.ReactNode
  header: (c: () => void) => React.ReactNode
  footer: React.ReactNode
  hasFooter?: boolean
  hasHeader?: boolean
  fullSnap?: boolean
}

const Sheet: React.FC<SheetProps> = ({
  header,
  footer,
  hasFooter,
  children,
  hasHeader,
  fullSnap
}) => {
  const { close } = useModalize()
  // We need to use state here
  // just so that when the sheet is closed,
  // it stays mounted until the animation finishes,
  // which calls `close`
  const [open, setOpen] = useState(true)
  const onDismiss = () => {
    setOpen(false)
  }
  const onClose = ({ type: action }: { type: string }) => {
    if (action !== 'CLOSE') return
    close()
  }
  return (
    <BottomSheet
      data-has-footer={hasFooter}
      data-has-header={hasHeader}
      open={open}
      onDismiss={onDismiss}
      expandOnContentDrag
      initialFocusRef={false}
      defaultSnap={defaultSnap}
      snapPoints={fullSnap ? topSnap : snapPoints}
      header={header ? header(onDismiss) : null}
      footer={footer}
      onSpringEnd={onClose}
    >
      <ThemeProvider theme={lakshmi} options={themeProviderOptions}>
        {children}
      </ThemeProvider>
    </BottomSheet>
  )
}

export default Sheet
